import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Phone, ArrowRight, Gift, X, Sparkles } from 'lucide-react';
import { api } from '../api';
import { StepChallenges, StepReport, ChallengesLoading, useFreeChallenges } from './FreeChallenges';
import SocialProofToasts from './SocialProofToasts';
import { useAuth } from '../context/AuthContext';

const SESSION_KEY = 'tbt_phone_capture_shown_v1';

const COUNTRY_CODES = [
  { code: '+91',  flag: 'IN',  label: 'India' },
  { code: '+1',   flag: 'US',  label: 'USA' },
  { code: '+44',  flag: 'UK',  label: 'UK' },
  { code: '+61',  flag: 'AU',  label: 'Australia' },
  { code: '+65',  flag: 'SG',  label: 'Singapore' },
  { code: '+971', flag: 'AE',  label: 'UAE' },
  { code: '+27',  flag: 'ZA',  label: 'South Africa' },
];

/**
 * Single-container acquisition funnel:
 *   phone → 5 challenges (1 per category) → report → onboarding → plans → signup
 * Never redirects to another page until the user is fully activated.
 */
export const PENDING_FUNNEL_KEY = 'tbt_pending_funnel_v1';
export const GUEST_JOURNEY_KEY = 'tbt_guest_journey_v1';

const PhoneCaptureModal = ({ autoOpen = true }) => {
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('phone'); // phone | challenge | report
  const [results, setResults] = useState([]);
  const challenges = useFreeChallenges();
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [fullMobile, setFullMobile] = useState('');

  useEffect(() => {
    // Wait for the session check: a stale/expired token in storage must not hide the funnel
    if (!autoOpen || !ready || user) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    const t = setTimeout(() => setOpen(true), 400);
    return () => clearTimeout(t);
  }, [autoOpen, ready, user]);

  const dismiss = () => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setOpen(false);
  };

  // Progress bar tiers
  const stepOrder = ['phone', 'challenge', 'report'];
  const stepIdx = stepOrder.indexOf(step);
  const showSocialProof = open && step === 'phone';

  // Modal width grows for content-heavier steps
  const widthCls = step === 'challenge' || step === 'report' ? 'max-w-lg' : 'max-w-md';

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
        <DialogContent
          data-testid="phone-capture-modal"
          className={`w-[calc(100vw-24px)] ${widthCls} bg-[#2A1810] border border-[#F4C430]/30 text-[#F5EDE0] p-0 overflow-hidden rounded-3xl [&>button:not([data-testid])]:hidden max-h-[92vh] overflow-y-auto`}
        >
          <DialogTitle className="sr-only">Take your free brain check</DialogTitle>
          <DialogDescription className="sr-only">
            Enter your mobile number, complete 5 quick brain challenges and unlock your free Brain Report.
          </DialogDescription>

          <button
            onClick={dismiss}
            aria-label="Close"
            data-testid="phone-capture-close"
            className="absolute top-3 right-3 h-9 w-9 rounded-full grid place-items-center border border-[#5a3d24] text-[#D9C9B3] hover:text-[#F4C430] hover:border-[#F4C430] z-10 bg-[#2A1810]/80 backdrop-blur"
          >
            <X size={16} />
          </button>

          {/* Progress bar across steps */}
          <div className="px-6 pt-6 sm:px-7 sm:pt-7 pr-14 sm:pr-14">
            <div className="flex items-center gap-1.5">
              {stepOrder.map((s, i) => (
                <div
                  key={s}
                  className={`h-1.5 rounded-full flex-1 transition-all ${
                    i <= stepIdx ? 'bg-[#F4C430]' : 'bg-[#4a3221]'
                  }`}
                  data-testid={`funnel-progress-${s}`}
                />
              ))}
            </div>
          </div>

          {step === 'phone' && (
            <StepPhone
              countryCode={countryCode}
              setCountryCode={setCountryCode}
              phone={phone}
              setPhone={setPhone}
              onSubmitted={(mobile) => { setFullMobile(mobile); setStep('challenge'); }}
              onDismiss={dismiss}
            />
          )}

          {step === 'challenge' && (
            challenges && challenges.length
              ? <StepChallenges challenges={challenges} onDone={(results) => { setResults(results); setStep('report'); }} />
              : <ChallengesLoading />
          )}

          {step === 'report' && (
            <StepReport
              results={results}
              ctaLabel="Personalise my training"
              note={<><span className="text-[#F5EDE0] font-medium">Next: a few quick questions</span> so we can tailor your puzzles and plan.</>}
              onContinue={() => {
                sessionStorage.setItem(GUEST_JOURNEY_KEY, JSON.stringify({ mobile: fullMobile, results, at: Date.now() }));
                sessionStorage.setItem(SESSION_KEY, '1');
                setOpen(false);
                setTimeout(() => navigate('/onboarding'), 150);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      <SocialProofToasts active={showSocialProof} />
    </>
  );
};

// -------- Step 1: Phone -----------------------------------------------------
const StepPhone = ({ countryCode, setCountryCode, phone, setPhone, onSubmitted, onDismiss }) => {
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 8) { setErr('Please enter a valid mobile number'); return; }
    setLoading(true); setErr(null);
    try {
      const full = `${countryCode}${digits}`;
      await api.capturePhoneLead({ mobile: full, countryCode, source: 'homepage_hero' });
      sessionStorage.setItem('tbt_lead_mobile_v1', full);
      onSubmitted(full);
    } catch (e2) {
      setErr(e2?.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="p-6 sm:p-7 pt-4" data-testid="funnel-step-phone">
      <div className="inline-flex items-center gap-2 rounded-full bg-[#3D2817] border border-[#F4C430]/40 px-3 py-1 text-[11px] text-[#F4C430]">
        <Sparkles size={12} /> Free Brain Check · 5 challenges
      </div>

      <h2 className="mt-4 font-serif text-2xl sm:text-3xl leading-tight">
        <span className="text-white">Start </span>
        <span className="text-[#F4C430] italic">Free Challenges</span>
        <span className="text-white">.</span>
      </h2>
      <p className="mt-2 text-[#B8A990] text-sm">
        Enter your mobile number to begin. 5 quick brain challenges, then your{' '}
        <span className="text-[#F5EDE0] font-medium">free Brain Report</span> — and a choice of the{' '}
        <span className="text-[#F4C430] font-semibold">Free Plan or a 7-day free trial</span>.
      </p>

      <form onSubmit={submit} className="mt-5 space-y-3" noValidate>
        <div className="flex gap-2">
          <div className="relative shrink-0">
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              data-testid="phone-capture-country"
              aria-label="Country code"
              className="appearance-none w-[92px] h-12 rounded-xl bg-[#3D2817] border border-[#5a3d24] text-[#F5EDE0] pl-3 pr-6 text-sm focus:outline-none focus:border-[#F4C430]"
            >
              {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
            </select>
            <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#8f7658]">▾</div>
          </div>
          <div className="relative flex-1">
            <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8f7658]" />
            <input
              data-testid="phone-capture-input"
              type="tel" inputMode="tel"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); if (err) setErr(null); }}
              placeholder="Enter your mobile number"
              className={`w-full h-12 rounded-xl bg-[#3D2817] border ${err ? 'border-red-400/60' : 'border-[#5a3d24]'} pl-10 pr-4 text-sm text-[#F5EDE0] placeholder:text-[#8f7658] focus:outline-none focus:border-[#F4C430]`}
              aria-invalid={!!err} autoComplete="tel" autoFocus
            />
          </div>
        </div>
        {err && (
          <div className="flex items-start gap-1.5 text-xs text-red-300" role="alert" data-testid="phone-capture-error">
            <span className="mt-0.5 inline-block h-1.5 w-1.5 rounded-full bg-red-400 shrink-0" />
            <span>{err}</span>
          </div>
        )}

        <Button
          type="submit" disabled={loading}
          data-testid="phone-capture-submit"
          className="w-full rounded-full bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] font-semibold h-12"
        >
          {loading ? 'Just a sec…' : (<><span>Start </span><span className="ml-1">Free Challenges</span><ArrowRight size={16} className="ml-1" /></>)}
        </Button>

        <div className="rounded-xl bg-[#3D2817] border border-[#4a3221] p-3 flex items-start gap-2.5">
          <Gift size={14} className="text-[#F4C430] mt-0.5 shrink-0" />
          <div className="text-xs text-[#B8A990]">
            <span className="text-[#F5EDE0]">5 quick challenges</span> · Unlock your Brain Report · Free Plan or a 7-day free trial — your choice.
          </div>
        </div>

        <button
          type="button" onClick={onDismiss}
          data-testid="phone-capture-skip"
          className="w-full text-center text-xs text-[#8f7658] hover:text-[#F4C430] py-1"
        >
          Not now, I'll explore first
        </button>
      </form>
    </div>
  );
};

export default PhoneCaptureModal;
